"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MonthlySalesData } from "@/services/dashboardService";
import { formatCurrency } from "@/lib/utils";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { format, parseISO } from "date-fns";

interface MonthlySalesChartProps {
  data: MonthlySalesData[];
  period: "weekly" | "monthly";
  onPeriodChange: (period: "weekly" | "monthly") => void;
  loading?: boolean;
}

export function MonthlySalesChart({
  data,
  period,
  onPeriodChange,
  loading = false,
}: MonthlySalesChartProps) {
  const formatXAxisDate = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      return period === "weekly" ? format(date, "MMM d") : format(date, "d");
    } catch {
      return dateStr;
    }
  };

  const totalCars = data.reduce((sum, d) => sum + d.count, 0);
  const totalRevenue = data.reduce((sum, d) => sum + d.revenue, 0);

  return (
    <Card className="lg:col-span-4">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base font-medium">
              Monthly Sales
            </CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={period === "weekly" ? "default" : "outline"}
              size="sm"
              onClick={() => onPeriodChange("weekly")}
            >
              Weekly
            </Button>
            <Button
              variant={period === "monthly" ? "default" : "outline"}
              size="sm"
              onClick={() => onPeriodChange("monthly")}
            >
              Monthly
            </Button>
          </div>
        </div>
        <div className="flex gap-6 mt-2 text-sm">
          <div>
            <span className="text-muted-foreground">Total Cars: </span>
            <span className="font-bold">{totalCars}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Revenue: </span>
            <span className="font-bold text-green-600">
              {formatCurrency(totalRevenue)}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            Loading...
          </div>
        ) : data.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No data available
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f0f0f0"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tickFormatter={formatXAxisDate}
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#999", fontSize: 12 }}
              />
              <YAxis
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#999", fontSize: 12 }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#999", fontSize: 12 }}
              />
              <Tooltip
                formatter={(value: number, name: string) => {
                  if (name === "revenue")
                    return [formatCurrency(value), "Revenue"];
                  return [value, "Cars Washed"];
                }}
                labelFormatter={(label) => {
                  try {
                    return format(parseISO(label), "MMM d, yyyy");
                  } catch {
                    return label;
                  }
                }}
                contentStyle={{
                  borderRadius: "8px",
                  border: "none",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="count"
                name="Cars Washed"
                stroke="#0ea5e9"
                strokeWidth={2}
                dot={{ fill: "#0ea5e9", r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="revenue"
                name="Revenue"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ fill: "#10b981", r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
